import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Download, Search } from 'lucide-react';
import { api, unwrap } from '@/api/client';
import type { components } from '@/api/schema';
import { Roles } from '@/auth/roles';
import { useCan } from '@/auth/useAuth';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge, Card, Skeleton } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { DataTable, EmptyRow, Field, Section, Switch, Textarea } from '@/components/ui/form-controls';
import { Input, NativeSelect } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { groupByTag, sideEffectVariant } from './logic';
import { SchemaTree } from './SchemaTree';

type ToolDto = components['schemas']['ToolDto'];
type ToolImportDto = components['schemas']['ToolImportDto'];

const toolKeys = {
  list: (source: string, q: string) => ['global', 'tools', source, q] as const,
  imports: () => ['global', 'tools', 'imports'] as const,
};

export function ToolsPage() {
  const { t } = useTranslation();
  const isAdmin = useCan({ roles: [Roles.PlatformAdmin] });
  const [source, setSource] = useState('');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const tools = useQuery({
    queryKey: toolKeys.list(source, q),
    queryFn: async () => unwrap(await api.GET('/v1/tools', { params: { query: { ...(source ? { source } : {}), ...(q ? { q } : {}) } } })),
  });
  const groups = useMemo(() => groupByTag(tools.data?.items ?? []), [tools.data]);
  const current = tools.data?.items.find((x) => x.name === selected) ?? null;

  return (
    <div className="flex h-full flex-col">
      <PageHeader help="tools"
        title={t('admin.tools.title')}
        description={t('admin.tools.description')}
        actions={
          isAdmin && (
            <Button onClick={() => setImportOpen(true)}>
              <Download /> {t('admin.tools.import')}
            </Button>
          )
        }
      />
      <div className="flex items-center gap-2 border-b px-6 py-3">
        <div className="relative w-72">
          <Search className="absolute left-2 top-2 size-4 text-muted-foreground" />
          <Input className="pl-8" placeholder={t('admin.tools.search')} value={q} onChange={(e) => setQ(e.target.value)} aria-label={t('admin.tools.search')} />
        </div>
        <NativeSelect value={source} onChange={(e) => setSource(e.target.value)} aria-label={t('admin.tools.source')}>
          <option value="">{t('admin.tools.allSources')}</option>
          <option value="jde">jde</option>
          <option value="platform">platform</option>
          <option value="composed">composed</option>
        </NativeSelect>
        <span className="text-xs text-muted-foreground">{t('admin.tools.count', { count: tools.data?.items.length ?? 0 })}</span>
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
        <div className="overflow-auto border-r p-4">
          {tools.isPending && <Skeleton className="h-40 w-full" />}
          {groups.length === 0 && !tools.isPending && <p className="text-sm text-muted-foreground">{t('admin.tools.empty')}</p>}
          {groups.map(([tag, items]) => (
            <div key={tag} className="mb-4">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{tag}</div>
              <ul className="flex flex-col">
                {items.map((tool) => (
                  <li key={tool.name}>
                    <button
                      type="button"
                      onClick={() => setSelected(tool.name)}
                      className={cn(
                        'flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted',
                        selected === tool.name && 'bg-muted',
                      )}
                    >
                      <span className="min-w-0">
                        <span className="block truncate font-mono text-xs">{tool.name}</span>
                        <span className="block truncate text-xs text-muted-foreground">{tool.summary}</span>
                      </span>
                      <span className="flex shrink-0 gap-1">
                        <Badge variant={sideEffectVariant(tool.sideEffect)}>{tool.sideEffect}</Badge>
                        {!tool.callableByAgents && <Badge variant="destructive">{t('admin.tools.notCallable')}</Badge>}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="overflow-auto p-6">
          {current ? <ToolDetail tool={current} canEdit={isAdmin} /> : <p className="text-sm text-muted-foreground">{t('admin.tools.selectHint')}</p>}
          {isAdmin && <ImportHistory />}
        </div>
      </div>
      {importOpen && <ImportDialog onClose={() => setImportOpen(false)} />}
    </div>
  );
}

function ToolDetail({ tool, canEdit }: { tool: ToolDto; canEdit: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [override, setOverride] = useState(tool.llmDescriptionOverride ?? '');
  const patch = useMutation({
    mutationFn: async (body: components['schemas']['ToolPatchRequest']) =>
      unwrap(await api.PATCH('/v1/tools/{name}', { params: { path: { name: tool.name } }, body })),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['global', 'tools'] });
      toast.success(t('admin.common.saved'));
    },
  });
  const base = { exposedToAgents: null, llmDescriptionOverride: null, authoritativeWhitelisted: null, clearLlmDescriptionOverride: null };

  return (
    <div className="flex flex-col gap-6" key={tool.name}>
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-mono text-sm font-semibold">{tool.name}</h2>
          <Badge>{tool.source}</Badge>
          <Badge variant={sideEffectVariant(tool.sideEffect)}>{tool.sideEffect}</Badge>
          {tool.method && (
            <span className="font-mono text-xs text-muted-foreground">
              {tool.method} {tool.path}
            </span>
          )}
        </div>
        <p className="mt-1 text-sm">{tool.summary}</p>
        {tool.description && <p className="mt-1 text-xs text-muted-foreground">{tool.description}</p>}
        <div className="mt-2 flex flex-wrap gap-1">
          {tool.scopes.map((s) => (
            <Badge key={s} variant="accent" className="font-mono">
              {s}
            </Badge>
          ))}
        </div>
        {tool.replacementTicket && <p className="mt-1 text-xs text-muted-foreground">{t('admin.tools.replacement', { ticket: tool.replacementTicket })}</p>}
      </div>

      {canEdit && !tool.internal && (
        <Card className="flex flex-col gap-3 p-4">
          <Switch
            label={t('admin.tools.exposed')}
            checked={tool.exposedToAgents}
            disabled={patch.isPending}
            onChange={(e) => patch.mutate({ ...base, exposedToAgents: e.target.checked })}
          />
          {tool.sideEffect === 'authoritative' && (
            <Switch
              label={
                <span>
                  {t('admin.tools.whitelisted')}
                  <span className="block text-xs text-destructive">{t('admin.tools.whitelistWarning')}</span>
                </span>
              }
              checked={tool.authoritativeWhitelisted}
              disabled={patch.isPending}
              onChange={(e) => {
                if (!e.target.checked || window.confirm(t('admin.tools.confirmWhitelist', { name: tool.name })))
                  patch.mutate({ ...base, authoritativeWhitelisted: e.target.checked });
              }}
            />
          )}
          <Field label={t('admin.tools.llmOverride')} htmlFor="llm-override" hint={t('admin.tools.llmOverrideHint')}>
            <Textarea id="llm-override" rows={3} value={override} onChange={(e) => setOverride(e.target.value)} placeholder={tool.llmDescription} />
          </Field>
          <div className="flex gap-2">
            <Button size="sm" disabled={patch.isPending} onClick={() => patch.mutate({ ...base, llmDescriptionOverride: override.trim() || null, clearLlmDescriptionOverride: override.trim() === '' })}>
              {t('admin.common.save')}
            </Button>
          </div>
        </Card>
      )}

      <Section title={t('admin.tools.llmDescription')}>
        <p className="whitespace-pre-wrap text-xs">{tool.llmDescription}</p>
      </Section>
      <Section title={t('admin.tools.input')}>
        <SchemaTree schema={tool.inputSchema} />
      </Section>
      <Section title={t('admin.tools.output')}>
        <SchemaTree schema={tool.outputSchema} />
        {tool.listMeta && (
          <p className="font-mono text-xs text-muted-foreground">
            {t('admin.tools.listMeta')}: {tool.listMeta.itemsPath}
            {tool.listMeta.cursorParam ? ` · cursor=${tool.listMeta.cursorParam}` : ''}
          </p>
        )}
      </Section>
    </div>
  );
}

export function DiffSummary({ diff }: { diff: components['schemas']['ToolDiff'] }) {
  const { t } = useTranslation();
  const empty = diff.added.length + diff.removed.length + diff.changed.length === 0;
  if (empty) return <p className="text-xs text-muted-foreground">{t('admin.tools.noChanges')}</p>;
  return (
    <div className="flex flex-col gap-1 text-xs" data-testid="diff-summary">
      {diff.added.map((n) => (
        <div key={`a-${n}`} className="font-mono text-success">
          + {n}
        </div>
      ))}
      {diff.removed.map((n) => (
        <div key={`r-${n}`} className="font-mono text-destructive">
          − {n}
        </div>
      ))}
      {diff.changed.map((c) => (
        <div key={`c-${c.name}`} className="font-mono">
          ~ {c.name} <span className="text-muted-foreground">({c.changedFields.join(', ')})</span>
        </div>
      ))}
    </div>
  );
}

function ImportHistory() {
  const { t } = useTranslation();
  const imports = useQuery({ queryKey: toolKeys.imports(), queryFn: async () => unwrap(await api.GET('/v1/tools/imports')) });
  const items = (imports.data as { items?: ToolImportDto[] } | undefined)?.items ?? [];
  return (
    <div className="mt-10">
      <Section title={t('admin.tools.importHistory')}>
        <DataTable head={[t('admin.tools.importedAt'), t('admin.tools.source'), t('admin.tools.specVersion'), t('admin.tools.toolCount'), t('admin.tools.changes')]}>
          {items.length === 0 && <EmptyRow colSpan={5}>{t('admin.tools.noImports')}</EmptyRow>}
          {items.map((i) => (
            <tr key={i.id}>
              <td className="text-xs">{new Date(i.importedAt).toLocaleString()}</td>
              <td>{i.source}</td>
              <td className="font-mono text-xs">{i.specVersion}</td>
              <td>{i.toolCount}</td>
              <td>
                <DiffSummary diff={i.diff} />
              </td>
            </tr>
          ))}
        </DataTable>
      </Section>
    </div>
  );
}

function ImportDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [url, setUrl] = useState('');
  const run = useMutation({
    mutationFn: async () => {
      const res = unwrap(await api.POST('/v1/tools/imports', { body: { source: 'jde', openApiUrl: url.trim() || null, tenantId: null } }));
      return res as unknown as ToolImportDto;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['global', 'tools'] });
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogTitle>{t('admin.tools.import')}</DialogTitle>
        <DialogDescription>{t('admin.tools.importHint')}</DialogDescription>
        <div className="mt-3 flex flex-col gap-3">
          <Field label={t('admin.tools.openApiUrl')} htmlFor="oa-url" hint={t('admin.tools.openApiUrlHint')}>
            <Input id="oa-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://connect.jobdesk.com/v1/docs/openapi.json" />
          </Field>
          {run.data?.diff && (
            <div role="status" className="rounded-md border p-2">
              <div className="mb-1 text-xs font-medium">{t('admin.tools.importDone', { count: run.data.toolCount })}</div>
              <DiffSummary diff={run.data.diff} />
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              {t('admin.common.close')}
            </Button>
            <Button onClick={() => run.mutate()} disabled={run.isPending}>
              {t('admin.tools.runImport')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
